import { createColumnHelper, flexRender } from "@tanstack/react-table";
import TextAreaCell from "../cell/TextAreaCell";
import { DataTableDefinition } from "@/v2/types/Datatable";
import { buildColumns } from "./buildColumns";
import { Link } from "react-router-dom";
import { Eye, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generateFilterColumn } from "../../filter";
import { Mobilization } from "@/v2/types";
import AccessControl from "../../rbac/AccessControl";
import MobilizationDeleteButton from "../../ui/button/delete/Mobilization";

type T = Mobilization;

const columnHelper = createColumnHelper<T>();

const MobilizationColumnDefinition: DataTableDefinition<T>[] = [
    {
        id: "sl",
        headingText: "Sl No",
        headingCellType: "small-cell",
        cellType: "text-center",
    },
    {
        id: "employee.full_name",
        headingText: "Employee Name",
    },
    {
        id: "employee_trade",
        headingText: "Employee Trade",
    },
    {
        id: "employee.nationality",
        headingText: "Nationality ",
    },
    {
        id: "mobilized_date",
        headingText: "Mobilization",
        cellType: "date",
    },
    {
        id: "de_mobilized_date",
        headingText: "Demobilization ",
        cellType: "date",
    },
    {
        id: "client.full_name",
        headingText: "Client ",
    },
    {
        id: "client.short_code",
        headingText: "Client Short Code ",
    },
    {
        id: "client_rate",
        headingText: "Client Rate",
        cellType: "money",
    },
    {
        id: "client_commission",
        headingText: "Client Commission",
        cellType: "money",
    },
    {
        id: "ar_coordinator.full_name",
        headingText: "AR Coordinator ",
    },
    {
        id: "supporting_coordinator.full_name",
        headingText: "AR Supporting Coordinator ",
    },
    {
        id: "supplier.full_name",
        headingText: "Supplier",
    },
    {
        id: "supplier.short_code",
        headingText: "Supplier Short Code",
    },
    {
        id: "supplier_rate",
        headingText: "Supplier Rate",
        cellType: "money",
    },
    {
        id: "supplier_commission",
        headingText: "Supplier Commission",
        cellType: "money",
    },
    {
        id: "food_accommodation_and_travel",
        headingText: "Food, Accommodation & Travel",
        headingCellType: "text-center",
        cellType: "money",
    },
    {
        id: "ar_four_percent",
        headingText: "AR 4%",
        headingCellType: "text-center",
        cellType: "money",
    },
    {
        id: "margin",
        headingText: "Margin",
        headingCellType: "text-center",
        cellType: "money",
    },
    {
        id: "client.vendor_code",
        headingText: "Vendor Code",
    },
    {
        id: "location",
        headingText: "Location",
    },
    {
        id: "remarks",
        headingText: "Remarks",
        cell: ({ value }) => <TextAreaCell value={value as string} />,
    },
    {
        id: "is_mobilized",
        headingText: "Status",
        cell: ({ row }) => {
            if (row.is_mobilized === true) {
                return (
                    <div className="flex items-center gap-2 font-bold text-green-500">
                        <div className="h-3 w-3 animate-pulse rounded-full bg-green-500" />
                        Mobilized
                    </div>
                );
            }

            if (row.is_mobilized === false) {
                return (
                    <div className="flex items-center gap-2 font-bold text-red-500">
                        <div className="h-3 w-3 rounded-full bg-red-500" />
                        Demobilized
                    </div>
                );
            }

            return <p>-</p>;
        },
    },
    {
        id: "actions",
        headingText: "Actions",
        headingCellType: "text-center",
        cell: ({ row }) =>
            flexRender(
                <div className="flex justify-center gap-2 text-black">
                    <Link to={`/mobilization-database/${row.id}`}>
                        <Button variant={"outline"} size={"sm"}>
                            <Eye className="size-4" />
                        </Button>
                    </Link>

                    <AccessControl
                        module="MOBILIZATION_MANAGEMENT"
                        requestedAccess={"PUT"}
                    >
                        {row.is_closed ? (
                            <Button variant={"outline"} size={"sm"} disabled>
                                <Pencil className="size-4" />
                            </Button>
                        ) : (
                            <Link to={`/mobilization-database/edit/${row.id}`}>
                                <Button variant={"outline"} size={"sm"}>
                                    <Pencil className="size-4" />
                                </Button>
                            </Link>
                        )}
                    </AccessControl>

                    <AccessControl
                        module="MOBILIZATION_MANAGEMENT"
                        requestedAccess={"DELETE"}
                    >
                        <MobilizationDeleteButton id={row.id} />
                    </AccessControl>
                </div>,
                {},
            ),
    },
];

const MobilizationFilterColumn = generateFilterColumn<Mobilization>(
    MobilizationColumnDefinition,
    {
        removeIds: ["location", "remarks"],
        addFilterId: {
            "employee.full_name": "employees.full_name",
            "employee.nationality": "employees.nationality",
            "employee.trade": "employees.trade",
            "ar_coordinator.full_name": "ar_coordinators.full_name",
            "supporting_coordinator.full_name":
                "supporting_coordinators.full_name",
            "client.full_name": "clients.full_name",
            "client.short_code": "clients.short_code",
            "supplier.full_name": "suppliers.full_name",
            "supplier.short_code": "suppliers.short_code",
            "employee.is_mobilized": "employees.is_mobilized",
        },
        renameMap: {
            "ar_coordinator.full_name": "AR Coordinator Name",
            "supporting_coordinator.full_name":
                "AR Supporting Coordinator Name",
            "client.full_name": "Client Name",
            "supplier.full_name": "Supplier Name",
        },
    },
    "MOBILIZATION_MANAGEMENT",
);

const MobilizationColumns = buildColumns<T>(
    columnHelper,
    MobilizationColumnDefinition,
    "MOBILIZATION_MANAGEMENT",
);

export { MobilizationFilterColumn, MobilizationColumns };
